/**
 * Wottz Vehicle Context v2.3
 * Persists vehicle selection across pages using localStorage (survives sessions).
 * Reads URL params (wv_make, wv_model, etc.) from email links to auto-populate.
 * Auto-selects correct product variant on product pages.
 * Shows vehicle confirmation banner (incl. V2L compatibility messaging).
 * Mini vehicle selector with reg plate lookup on product pages.
 * Injects cart line properties for marketing.
 *
 * Theme-update safe: loaded via custom sections only.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'wottz_vehicle';
  var CAR_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 24" width="22" height="17" fill="currentColor"><path d="M25.4 8.6l-2.2-5.2C22.7 2.2 21.5 1.5 20.2 1.5H11.8c-1.3 0-2.5.7-3 1.9L6.6 8.6C5.1 9.2 4 10.7 4 12.5v6c0 .8.7 1.5 1.5 1.5h1c.8 0 1.5-.7 1.5-1.5V18h16v.5c0 .8.7 1.5 1.5 1.5h1c.8 0 1.5-.7 1.5-1.5v-6c0-1.8-1.1-3.3-2.6-3.9zM10.2 4.3c.2-.4.6-.7 1-.7h9.6c.4 0 .8.3 1 .7L23.4 8H8.6l1.6-3.7zM8.5 15.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm15 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z"/></svg>';
  var DVLA_ENDPOINT = 'https://fast-ibex-116.convex.site/api/dvla-lookup';
  var VEHICLE_JSON_URL = 'https://cdn.shopify.com/s/files/1/0791/0250/9395/files/cable-finder-vehicles_3d0e26b0-25c5-4299-8806-38161bee4493.json?v=1777642594';

  /* ─── Storage (localStorage for cross-session persistence) ─── */
  var WottzVehicle = {
    save: function (data) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        // Also write to sessionStorage for backward compat with cable finder
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        window.dispatchEvent(new CustomEvent('wottz:vehicle-changed', { detail: data }));
      } catch (e) { /* private browsing */ }
    },
    get: function () {
      try {
        // Prefer localStorage (persists), fall back to sessionStorage
        var raw = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) { return null; }
    },
    clear: function () {
      try {
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);
        window.dispatchEvent(new CustomEvent('wottz:vehicle-changed', { detail: null }));
      } catch (e) { /* */ }
    }
  };

  /* ─── URL Param Detection (email link click-through) ─── */
  function checkUrlParams() {
    try {
      var params = new URLSearchParams(window.location.search);
      var make = params.get('wv_make');
      var model = params.get('wv_model');
      if (!make || !model) return null;

      var data = {
        make: make,
        model: model,
        connector: params.get('wv_connector') || 'Type 2',
        chargeRate: params.get('wv_rate') || null,
        v2lOption: params.get('wv_v2l') || null,
        source: 'email'
      };

      // Save to storage
      WottzVehicle.save(data);

      // Clean vehicle params from URL (keep variant= etc.)
      var url = new URL(window.location);
      ['wv_make', 'wv_model', 'wv_connector', 'wv_rate', 'wv_v2l'].forEach(function(p) {
        url.searchParams.delete(p);
      });
      history.replaceState(null, '', url.toString());

      return data;
    } catch (e) {
      return null;
    }
  }

  /* ─── Option Mapping ─── */
  var OPTION_MAP = {
    'wottz-type-2-to-type-2-ev-charging-cable': function (v) {
      if (v.connector === 'Type 1') return 'Type 1 to Type 2 | 7.4kW';
      return parseFloat(v.chargeRate) >= 11
        ? 'Type 2 to Type 2 | Home & Public (22kW)'
        : 'Type 2 to Type 2 | Home (7.4kW)';
    },
    'type-2-tethered-ev-charging-cable': function (v) {
      if (v.connector === 'Type 1') return 'Type 1 to Tethered (7.4Kw)';
      return parseFloat(v.chargeRate) >= 11
        ? 'Type 2 to Tethered (22kW)'
        : 'Type 2 to Tethered (7.4kW)';
    },
    'wottz-portable-charger-1': function (v) {
      return v.connector === 'Type 1' ? 'Type 1' : 'Type 2';
    },
    'wottz-v2l-vehicle-to-load-discharge-cable': function (v) {
      return v.v2lOption || null;
    }
  };

  /* ─── V2L Product Handle ─── */
  var V2L_HANDLE = 'wottz-v2l-vehicle-to-load-discharge-cable';

  /* ─── Product Page Detection ─── */
  function getProductHandle() {
    var el = document.querySelector('[data-wvc-product-handle]');
    if (el) return el.getAttribute('data-wvc-product-handle');
    var canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      var match = canonical.href.match(/\/products\/([^?#]+)/);
      if (match) return match[1];
    }
    var urlMatch = window.location.pathname.match(/\/products\/([^?#/]+)/);
    if (urlMatch) return urlMatch[1];
    return null;
  }

  /* ─── Auto-Select Variant ─── */
  function autoSelectVariant(handle, vehicle) {
    var mapFn = OPTION_MAP[handle];
    if (!mapFn) return;

    var targetValue = mapFn(vehicle);
    if (!targetValue) return;

    var attempts = 0;
    var maxAttempts = 20;

    function trySelect() {
      attempts++;
      var picker = document.querySelector('variant-picker');
      if (!picker && attempts < maxAttempts) {
        setTimeout(trySelect, 150);
        return;
      }
      if (!picker) return;

      var firstFieldset = picker.querySelector('fieldset[data-option-index="option1"]');
      if (!firstFieldset) return;

      var radios = firstFieldset.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < radios.length; i++) {
        var val = radios[i].getAttribute('data-option-value') || radios[i].value;
        if (val === targetValue && !radios[i].checked) {
          radios[i].checked = true;
          radios[i].dispatchEvent(new Event('change', { bubbles: true }));
          break;
        }
      }
    }

    trySelect();
  }

  /* ─── Cart Line Properties ─── */
  function injectCartProperties(vehicle) {
    // Target the main product form (product-form custom element) to avoid duplicates
    var forms = document.querySelectorAll('form[is="product-form"]');
    if (!forms.length) {
      forms = document.querySelectorAll('form[action="/cart/add"]');
    }

    for (var f = 0; f < forms.length; f++) {
      var form = forms[f];

      // Remove any existing vehicle properties first
      var existing = form.querySelectorAll('.wvc-cart-prop');
      for (var i = 0; i < existing.length; i++) { existing[i].remove(); }

      if (!vehicle) continue;

      var vehicleName = vehicle.make + (vehicle.model ? ' ' + vehicle.model : '');

      // Only record Vehicle name and registration (if available)
      var props = [
        { name: 'properties[Vehicle]', value: vehicleName }
      ];

      // Include reg plate if stored
      var regPlate = vehicle.regPlate || '';
      if (regPlate) {
        props.push({ name: 'properties[Registration]', value: regPlate });
      }

      for (var j = 0; j < props.length; j++) {
        if (!props[j].value) continue;
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = props[j].name;
        input.value = props[j].value;
        input.className = 'wvc-cart-prop';
        form.appendChild(input);
      }
    }
  }

  /* ─── Vehicle Banner ─── */
  function renderBanner(vehicle) {
    var container = document.getElementById('wvc-banner');
    if (!container) return;

    if (!vehicle) {
      container.innerHTML = '';
      container.style.display = 'none';
      return;
    }

    var handle = getProductHandle();
    var isV2L = handle === V2L_HANDLE;
    var hasV2L = !!(vehicle.v2lOption);

    var name = vehicle.make + (vehicle.model ? ' ' + vehicle.model : '');
    var spec = vehicle.connector + ' connector';
    if (vehicle.chargeRate) spec += ' \u2022 up to ' + vehicle.chargeRate + 'kW';

    // Determine check message
    var checkHTML = '';
    if (isV2L && !hasV2L) {
      // V2L page but vehicle doesn't support V2L
      checkHTML =
        '<span class="wvc-banner__nocompat">' +
          '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>' +
          ' V2L is not available for this vehicle' +
        '</span>';
    } else {
      checkHTML = '<span class="wvc-banner__check">\u2713 Correct cable pre-selected</span>';
    }

    container.innerHTML =
      '<div class="wvc-banner' + (isV2L && !hasV2L ? ' wvc-banner--nocompat' : '') + '">' +
        '<div class="wvc-banner__left">' +
          '<span class="wvc-banner__icon">' + CAR_SVG + '</span>' +
          '<div class="wvc-banner__text">' +
            '<strong class="wvc-banner__name">' + name + '</strong>' +
            '<span class="wvc-banner__spec">' + spec + '</span>' +
          '</div>' +
        '</div>' +
        '<div class="wvc-banner__right">' +
          checkHTML +
          '<button class="wvc-banner__change" type="button">Change</button>' +
        '</div>' +
      '</div>';

    container.style.display = 'block';

    // Change button handler
    var changeBtn = container.querySelector('.wvc-banner__change');
    if (changeBtn) {
      changeBtn.addEventListener('click', function () {
        toggleMiniSelector(true);
      });
    }
  }

  /* ─── Custom Dropdown (avoids theme's bb-customSelect hijacking <select>) ─── */
  function createDropdown(id, placeholder) {
    return (
      '<div class="wvc-dd" id="' + id + '">' +
        '<button type="button" class="wvc-dd__trigger" data-placeholder="' + placeholder + '">' +
          '<span class="wvc-dd__label">' + placeholder + '</span>' +
          '<svg class="wvc-dd__arrow" width="10" height="6" viewBox="0 0 10 6"><path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>' +
        '</button>' +
        '<ul class="wvc-dd__list" style="display:none"></ul>' +
      '</div>'
    );
  }

  function ddSetOptions(id, options) {
    var dd = document.getElementById(id);
    if (!dd) return;
    var list = dd.querySelector('.wvc-dd__list');
    var trigger = dd.querySelector('.wvc-dd__trigger');
    var label = dd.querySelector('.wvc-dd__label');
    var placeholder = trigger.getAttribute('data-placeholder');

    list.innerHTML = '';
    label.textContent = placeholder;
    dd.dataset.value = '';
    dd.classList.remove('wvc-dd--disabled');

    if (!options || options.length === 0) {
      dd.classList.add('wvc-dd--disabled');
      return;
    }

    options.forEach(function (opt) {
      var li = document.createElement('li');
      li.className = 'wvc-dd__item';
      li.textContent = opt.label || opt.value;
      li.dataset.value = opt.value;
      li.addEventListener('click', function () {
        label.textContent = this.textContent;
        dd.dataset.value = this.dataset.value;
        list.style.display = 'none';
        dd.classList.remove('wvc-dd--open');
        // Fire custom change event
        dd.dispatchEvent(new CustomEvent('dd:change', { detail: { value: this.dataset.value } }));
      });
      list.appendChild(li);
    });
  }

  function ddGetValue(id) {
    var dd = document.getElementById(id);
    return dd ? (dd.dataset.value || '') : '';
  }

  // Toggle dropdown open/close
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('.wvc-dd__trigger');
    if (trigger) {
      var dd = trigger.closest('.wvc-dd');
      if (dd.classList.contains('wvc-dd--disabled')) return;
      var list = dd.querySelector('.wvc-dd__list');
      var isOpen = dd.classList.contains('wvc-dd--open');
      // Close all other dropdowns
      document.querySelectorAll('.wvc-dd--open').forEach(function (d) {
        d.classList.remove('wvc-dd--open');
        d.querySelector('.wvc-dd__list').style.display = 'none';
      });
      if (!isOpen) {
        dd.classList.add('wvc-dd--open');
        list.style.display = 'block';
      }
      return;
    }
    // Click outside closes all dropdowns
    if (!e.target.closest('.wvc-dd__list')) {
      document.querySelectorAll('.wvc-dd--open').forEach(function (d) {
        d.classList.remove('wvc-dd--open');
        d.querySelector('.wvc-dd__list').style.display = 'none';
      });
    }
  });

  /* ─── Mini Vehicle Selector (with reg plate) ─── */
  var vehicleData = null;

  function renderMiniSelector() {
    var container = document.getElementById('wvc-mini-selector');
    if (!container) return;

    var stored = WottzVehicle.get();
    var hasVehicle = !!stored;

    container.innerHTML =
      '<div class="wvc-mini" id="wvc-mini-panel"' + (hasVehicle ? ' style="display:none"' : '') + '>' +
        '<div class="wvc-mini__inner">' +
          '<div class="wvc-mini__header">' +
            '<span class="wvc-mini__icon">' + CAR_SVG + '</span>' +
            '<span class="wvc-mini__title">What car do you drive?</span>' +
          '</div>' +
          '<div class="wvc-mini__body">' +
            '<div class="wvc-mini__reg-row">' +
              '<div class="wvc-mini__reg-plate">' +
                '<span class="wvc-mini__reg-gb">GB</span>' +
                '<input type="text" id="wvc-mini-reg" class="wvc-mini__reg-input" placeholder="ENTER REG" maxlength="8" autocomplete="off" />' +
              '</div>' +
              '<button id="wvc-mini-reg-btn" class="wvc-mini__reg-btn" type="button">Go</button>' +
            '</div>' +
            '<div class="wvc-mini__divider"><span>or</span></div>' +
            '<div class="wvc-mini__row">' +
              createDropdown('wvc-mini-make', 'Select make...') +
              createDropdown('wvc-mini-model', 'Select model...') +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div id="wvc-mini-error" class="wvc-mini__error" style="display:none"></div>' +
      '</div>';

    // Model starts disabled
    document.getElementById('wvc-mini-model').classList.add('wvc-dd--disabled');

    loadVehicleData(function () {
      populateMiniMakes();
    });

    // Reg plate lookup handlers
    var regBtn = document.getElementById('wvc-mini-reg-btn');
    var regInput = document.getElementById('wvc-mini-reg');
    if (regBtn) {
      regBtn.addEventListener('click', onMiniRegLookup);
    }
    if (regInput) {
      regInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          onMiniRegLookup();
        }
      });
      // Auto-uppercase as user types
      regInput.addEventListener('input', function () {
        this.value = this.value.toUpperCase();
      });
    }
  }

  function showMiniError(msg) {
    var el = document.getElementById('wvc-mini-error');
    if (el) {
      el.textContent = msg;
      el.style.display = 'block';
    }
  }

  function hideMiniError() {
    var el = document.getElementById('wvc-mini-error');
    if (el) el.style.display = 'none';
  }

  /* ─── DVLA Make Matching ─── */
  var DVLA_MAKE_MAP = {
    'MERCEDES-BENZ': 'Mercedes-Benz',
    'MERCEDES': 'Mercedes-Benz',
    'VOLKSWAGEN': 'Volkswagen',
    'VW': 'Volkswagen',
    'BMW': 'BMW',
    'LAND ROVER': 'Land Rover',
    'MINI': 'MINI',
    'ROLLS ROYCE': 'Rolls-Royce',
    'ROLLS-ROYCE': 'Rolls-Royce',
    'ALFA ROMEO': 'Alfa Romeo',
    'ASTON MARTIN': 'Aston Martin',
    'DS': 'DS',
    'DS AUTOMOBILES': 'DS',
    'MG': 'MG',
    'MG MOTOR UK': 'MG',
    'GREAT WALL': 'ORA'
  };

  function matchDvlaMake(dvlaMake) {
    if (!vehicleData || !dvlaMake) return null;
    var upper = dvlaMake.toUpperCase().trim();
    var makes = Object.keys(vehicleData.m);

    // Exact match (case-insensitive)
    for (var i = 0; i < makes.length; i++) {
      if (makes[i].toUpperCase() === upper) return makes[i];
    }
    // Known mapping
    if (DVLA_MAKE_MAP[upper] && vehicleData.m[DVLA_MAKE_MAP[upper]]) {
      return DVLA_MAKE_MAP[upper];
    }
    // Substring match
    for (var j = 0; j < makes.length; j++) {
      if (upper.indexOf(makes[j].toUpperCase()) >= 0 || makes[j].toUpperCase().indexOf(upper) >= 0) {
        return makes[j];
      }
    }
    return null;
  }

  function autoRecommendFromDvla(matchedMake, dvlaData) {
    if (!vehicleData || !vehicleData.m[matchedMake]) return null;
    var models = vehicleData.m[matchedMake];

    // Find most common connector and charge rate for this make
    var connCounts = {};
    var rateCounts = {};
    for (var i = 0; i < models.length; i++) {
      var c = models[i][1];
      var r = models[i][2];
      connCounts[c] = (connCounts[c] || 0) + 1;
      rateCounts[r] = (rateCounts[r] || 0) + 1;
    }

    var connector = Object.keys(connCounts).sort(function (a, b) { return connCounts[b] - connCounts[a]; })[0];
    var chargeRate = Object.keys(rateCounts).sort(function (a, b) { return rateCounts[b] - rateCounts[a]; })[0];

    // Check if any models for this make have V2L
    var v2lOption = null;
    for (var j = 0; j < models.length; j++) {
      if (models[j][3]) {
        v2lOption = models[j][3];
        break;
      }
    }

    // Try to find specific model match by DVLA model name
    var dvlaModel = (dvlaData.model || '').trim();
    var bestModel = null;
    if (dvlaModel) {
      var dvlaModelUpper = dvlaModel.toUpperCase();
      for (var k = 0; k < models.length; k++) {
        var mUpper = models[k][0].toUpperCase();
        if (mUpper === dvlaModelUpper || mUpper.indexOf(dvlaModelUpper) >= 0 || dvlaModelUpper.indexOf(mUpper) >= 0) {
          bestModel = models[k];
          break;
        }
      }
    }

    if (bestModel) {
      connector = bestModel[1];
      chargeRate = bestModel[2];
      v2lOption = bestModel[3] || null;
      return {
        make: matchedMake,
        model: bestModel[0],
        connector: connector === 2 || connector === '2' ? 'Type 2' : 'Type 1',
        chargeRate: chargeRate,
        v2lOption: v2lOption
      };
    }

    // Fall back to make-level best guess
    return {
      make: matchedMake,
      model: dvlaData.model || matchedMake,
      connector: connector === 2 || connector === '2' ? 'Type 2' : 'Type 1',
      chargeRate: chargeRate,
      v2lOption: v2lOption
    };
  }

  /* ─── Mini Reg Plate Lookup ─── */
  function onMiniRegLookup() {
    var regInput = document.getElementById('wvc-mini-reg');
    var regBtn = document.getElementById('wvc-mini-reg-btn');
    if (!regInput) return;

    var reg = regInput.value.replace(/\s/g, '').toUpperCase();
    if (!reg || reg.length < 2) return;

    hideMiniError();
    regBtn.disabled = true;
    regBtn.textContent = '...';

    fetch(DVLA_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ registrationNumber: reg })
    })
    .then(function (r) { return r.json(); })
    .then(function (data) {
      regBtn.disabled = false;
      regBtn.textContent = 'Go';

      if (!data.success) {
        showMiniError("Couldn't find that registration. Try again or select below.");
        return;
      }

      var fuel = (data.fuelType || '').toUpperCase();
      var isEV = fuel === 'ELECTRICITY' || fuel.indexOf('ELECTRIC') >= 0;

      if (!isEV) {
        showMiniError('That vehicle appears to be ' + (data.fuelType || 'petrol/diesel').toLowerCase() + '. EV cables are for electric vehicles.');
        return;
      }

      var matchedMake = matchDvlaMake(data.make);
      if (!matchedMake) {
        showMiniError("We found your " + data.make + " but don't have cable data for this make yet. Select below.");
        return;
      }

      var result = autoRecommendFromDvla(matchedMake, data);
      if (!result) {
        showMiniError("Couldn't match a model. Please select below.");
        return;
      }

      // Save reg plate with the vehicle data
      result.regPlate = reg;

      // Save and apply
      WottzVehicle.save(result);
      var handle = getProductHandle();
      if (handle) autoSelectVariant(handle, result);
      renderBanner(result);
      injectCartProperties(result);
      toggleMiniSelector(false);
    })
    .catch(function () {
      regBtn.disabled = false;
      regBtn.textContent = 'Go';
      showMiniError('Connection error. Please try again or select below.');
    });
  }

  function toggleMiniSelector(show) {
    var panel = document.getElementById('wvc-mini-panel');
    if (panel) panel.style.display = show ? 'block' : 'none';
    if (show) hideMiniError();
  }

  function loadVehicleData(cb) {
    if (vehicleData) { cb(); return; }
    fetch(VEHICLE_JSON_URL)
      .then(function (r) { return r.json(); })
      .then(function (d) { vehicleData = d; cb(); })
      .catch(function () { /* silently fail */ });
  }

  function populateMiniMakes() {
    if (!vehicleData) return;

    var makes = Object.keys(vehicleData.m).sort();
    var makeOpts = makes.map(function (m) { return { value: m, label: m }; });
    ddSetOptions('wvc-mini-make', makeOpts);

    // When make changes, populate model dropdown
    var makeDd = document.getElementById('wvc-mini-make');
    if (makeDd) {
      makeDd.addEventListener('dd:change', function (e) {
        var make = e.detail.value;
        hideMiniError();

        if (!make || !vehicleData.m[make]) {
          ddSetOptions('wvc-mini-model', []);
          document.getElementById('wvc-mini-model').classList.add('wvc-dd--disabled');
          return;
        }

        var models = vehicleData.m[make];
        var modelOpts = models.map(function (m) { return { value: m[0], label: m[0] }; });
        ddSetOptions('wvc-mini-model', modelOpts);
      });
    }

    // When model changes, save vehicle and apply
    var modelDd = document.getElementById('wvc-mini-model');
    if (modelDd) {
      modelDd.addEventListener('dd:change', function (e) {
        var make = ddGetValue('wvc-mini-make');
        var model = e.detail.value;
        if (!make || !model || !vehicleData.m[make]) return;

        var vehicle = vehicleData.m[make].find(function (v) { return v[0] === model; });
        if (!vehicle) return;

        var connector = vehicle[1] === 2 || vehicle[1] === '2' ? 'Type 2' : 'Type 1';
        var chargeRate = vehicle[2];
        var v2lOption = vehicle[3] || null;

        var data = {
          make: make,
          model: model,
          connector: connector,
          chargeRate: chargeRate,
          v2lOption: v2lOption
        };

        WottzVehicle.save(data);

        var handle = getProductHandle();
        if (handle) autoSelectVariant(handle, data);
        renderBanner(data);
        injectCartProperties(data);
        toggleMiniSelector(false);
      });
    }
  }

  /* ─── Init ─── */
  function init() {
    // Check URL params first (email click-through) — overrides stored vehicle
    var urlVehicle = checkUrlParams();
    var vehicle = urlVehicle || WottzVehicle.get();
    var handle = getProductHandle();

    var bannerEl = document.getElementById('wvc-banner');
    var miniEl = document.getElementById('wvc-mini-selector');

    if (bannerEl || miniEl) {
      if (vehicle && bannerEl) {
        renderBanner(vehicle);
      }
      if (miniEl) {
        renderMiniSelector();
      }
      if (vehicle && handle) {
        autoSelectVariant(handle, vehicle);
      }
      // Inject cart properties on load
      if (vehicle) {
        injectCartProperties(vehicle);
      }
    }

    // Listen for vehicle changes
    window.addEventListener('wottz:vehicle-changed', function (e) {
      var v = e.detail;
      renderBanner(v);
      injectCartProperties(v);
      if (v) {
        toggleMiniSelector(false);
        var h = getProductHandle();
        if (h) autoSelectVariant(h, v);
      } else {
        toggleMiniSelector(true);
      }
    });
  }

  // Expose globally
  window.WottzVehicle = WottzVehicle;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
